/* Projections. The correctness backstop for the whole offline design.
 *
 * "Current" is never stored as truth. The PRD says so directly: the cached
 * current_* columns are "a read optimization only", and status_log "remains the
 * source of truth for conflict resolution and audit."
 *
 * One rule, written once, tested forever:
 *
 *     current = the value from the log row with max(changed_at, id-as-tiebreak)
 *
 * The id tiebreak is what makes out-of-order arrivals self-heal. UUIDv7 is
 * time-ordered, so when two devices stamp the same millisecond the later writer
 * still wins deterministically — and both devices reach the same answer without
 * talking to each other. That property is why there are no conflicts to
 * resolve: two people correcting the same field produce two rows, the
 * projection picks one, and the history shows both.
 *
 * This module is shared so the desk and the phone agree on what they're
 * looking at. The desk renders server projections, not these — but when the
 * two disagree on screen, that is the nightly reconcile job's bug, and the
 * desk is deliberately where you'd see it.
 */

import { median } from './aggregate';
import type {
  CorrectionLog,
  Evidence,
  FieldAssignment,
  PriorityRanking,
  PriorityRectified,
  StatusLog,
  Uuid,
} from './types';

/** The comparator. Everything in this file reduces to it. */
export function later<T extends { id: string; at: string }>(a: T, b: T): T {
  if (a.at !== b.at) return a.at > b.at ? a : b;
  return a.id > b.id ? a : b;
}

/** Latest value of one status field on one entity. */
export function projectStatus(
  logs: StatusLog[],
  entityId: Uuid,
  statusField: string,
  fallback: string,
): string {
  const rows = logs
    .filter((l) => l.entity_id === entityId && l.status_field === statusField)
    .map((l) => ({ ...l, at: l.changed_at }));
  if (rows.length === 0) return fallback;
  return rows.reduce(later).new_status;
}

/**
 * Apply the correction log over an immutable evidence record.
 *
 * The original is never mutated — this returns the projected read view and the
 * untouched original stays underneath it forever. That distinction is the whole
 * product: the desk's ledger screen renders both.
 *
 * Field paths are dotted and address into the typed sub-payload:
 *   notes_text · rock_observation.rock_type · structural_feature.strike_dip ·
 *   geometry (whole value — see the note below)
 */
export function projectEvidence(
  original: Evidence,
  corrections: CorrectionLog[],
): Evidence {
  const mine = corrections.filter((c) => c.evidence_id === original.id);
  if (mine.length === 0) return original;

  // One winner per field.
  const byField = new Map<string, CorrectionLog & { at: string }>();
  for (const c of mine) {
    const row = { ...c, at: c.corrected_at };
    const held = byField.get(c.corrected_field);
    byField.set(c.corrected_field, held ? later(held, row) : row);
  }

  const out: Evidence = {
    ...original,
    rock_observation: original.rock_observation && { ...original.rock_observation },
    structural_feature: original.structural_feature && {
      ...original.structural_feature,
    },
    geometry: { ...original.geometry } as Evidence['geometry'],
  };

  for (const [field, c] of byField) {
    const parts = field.split('.');
    if (parts.length === 1) {
      (out as unknown as Record<string, unknown>)[field] = c.new_value;
    } else {
      const parent = (out as unknown as Record<string, Record<string, unknown>>)[parts[0]];
      if (parent) parent[parts[1]] = c.new_value;
    }
  }

  return out;
}

/**
 * PRD gap #2, handled here rather than papered over: geometry is declared
 * immutable once synced, but gps_accuracy_m exists precisely because geometry
 * is sometimes wrong. A fault trace walked at ±10m will need correcting.
 *
 * The path taken: geometry corrections are ordinary correction_log rows on the
 * field `geometry`. The original point is never overwritten — it stays in the
 * record and both are visible on the ledger. "Immutable" holds in the sense
 * that matters (the original bytes survive); the read view moves.
 */
export const wasRelocated = (e: Evidence, corrections: CorrectionLog[]): boolean =>
  corrections.some((c) => c.evidence_id === e.id && c.corrected_field === 'geometry');

export const correctionsFor = (id: Uuid, all: CorrectionLog[]): CorrectionLog[] =>
  all
    .filter((c) => c.evidence_id === id)
    .sort((a, b) => (a.corrected_at < b.corrected_at ? -1 : 1));

/* ── Media ──────────────────────────────────────────────────────────────── */

/** Live photos. Superseded ones stay in the record but off the screen. */
export const livePhotos = (e: Evidence) =>
  e.media.filter((m) => m.kind === 'photo' && !m.superseded_by);

export const supersededPhotos = (e: Evidence) =>
  e.media.filter((m) => m.kind === 'photo' && m.superseded_by);

export const audioNotes = (e: Evidence) => e.media.filter((m) => m.kind === 'audio');

/* ── Assignments ────────────────────────────────────────────────────────── */

export function projectAssignment(
  a: FieldAssignment,
  logs: StatusLog[],
): FieldAssignment {
  return {
    ...a,
    current_status: projectStatus(
      logs,
      a.id,
      'current_status',
      a.current_status,
    ) as FieldAssignment['current_status'],
  };
}

/** Linked evidence count — the assignment progress readout. */
export const linkedEvidence = (a: FieldAssignment, evidence: Evidence[]): Evidence[] =>
  evidence.filter((e) => e.source_assignment_id === a.id);

/**
 * Expected tags versus what was actually found (Addendum A4.3).
 * The diff is the interesting part: "expected chl-ser, found chl-ser ✓ + jasper".
 */
export function tagDiff(a: FieldAssignment, found: Evidence[]) {
  const expected = new Set([
    ...(a.expected_alteration_type ?? []),
    ...(a.expected_rock_type ? [a.expected_rock_type] : []),
  ]);
  const actual = new Set(
    found.flatMap((e) => [
      ...(e.rock_observation?.alteration_type ?? []),
      ...(e.rock_observation?.rock_type ? [e.rock_observation.rock_type] : []),
    ]),
  );
  return {
    met: [...expected].filter((t) => actual.has(t)),
    missing: [...expected].filter((t) => !actual.has(t)),
    unexpected: [...actual].filter((t) => !expected.has(t)),
  };
}

/* ── Priorities. Append-only by construction, so "my current ranking" is
      itself a projection. ────────────────────────────────────────────────── */

/** One user's latest rank per item. */
export function projectRankings(
  rankings: PriorityRanking[],
  userId: Uuid,
): Map<Uuid, number> {
  const byItem = new Map<Uuid, PriorityRanking & { at: string }>();
  for (const r of rankings.filter((x) => x.user_id === userId)) {
    const row = { ...r, at: r.ranked_at };
    const held = byItem.get(r.item_id);
    byItem.set(r.item_id, held ? later(held, row) : row);
  }
  return new Map([...byItem].map(([k, v]) => [k, v.rank]));
}

/** The standing rectified order, or null if Patrick hasn't ruled yet. */
export function projectRectified(
  rows: PriorityRectified[],
  propertyId: string,
): PriorityRectified | null {
  const mine = rows
    .filter((r) => r.property_id === propertyId)
    .map((r) => ({ ...r, at: r.rectified_at }));
  return mine.length ? mine.reduce(later) : null;
}

/**
 * Divergence hint (Addendum A4.4): items where the team's median rank differs
 * from the rectified rank by 3 or more get a quiet marker. It is an invitation
 * to argue with the order, not a warning.
 */
export function divergence(
  itemId: Uuid,
  rankings: PriorityRanking[],
  rectifiedOrder: Uuid[],
): number | null {
  const rectifiedRank = rectifiedOrder.indexOf(itemId);
  if (rectifiedRank < 0) return null;

  const latestPerUser = new Map<Uuid, PriorityRanking & { at: string }>();
  for (const r of rankings.filter((x) => x.item_id === itemId)) {
    const row = { ...r, at: r.ranked_at };
    const held = latestPerUser.get(r.user_id);
    latestPerUser.set(r.user_id, held ? later(held, row) : row);
  }
  const ranks = [...latestPerUser.values()].map((r) => r.rank).sort((a, b) => a - b);
  if (ranks.length === 0) return null;

  // Was a second, hand-rolled median sitting a few files from the shared one.
  // Two implementations of an average is how two screens end up disagreeing.
  return Math.abs((median(ranks) ?? 0) - (rectifiedRank + 1));
}

export const DIVERGENCE_THRESHOLD = 3;

/* ── The latency KPI (SPEC §10). Rendered honestly, both clocks shown. ──── */

export const syncLatencyMs = (e: Evidence): number | null =>
  e.received_at ? Date.parse(e.received_at) - Date.parse(e.captured_at) : null;

/** SPEC §10: tags + photo + accuracy ≤10m. The Phase 4 go/no-go dial. */
export function isTrainingGrade(e: Evidence): boolean {
  const hasTags =
    (e.rock_observation?.alteration_type?.length ?? 0) > 0 ||
    !!e.structural_feature?.feature_type;
  return hasTags && livePhotos(e).length > 0 && e.gps_accuracy_m <= 10;
}

/**
 * Time from an assignment being raised to the first piece of evidence captured
 * against it. Null when nothing has been captured yet.
 *
 * This was open-coded identically in Dashboard and Board, and the two had
 * already drifted: Board filtered to completed assignments first, Dashboard did
 * not, so the desk showed two different numbers both labelled "median cycle
 * time". The primitive lives here now; which assignments to feed it is a
 * question each screen answers out loud rather than by accident.
 */
export const timeToFirstEvidenceMs = (
  a: FieldAssignment,
  evidence: Evidence[],
): number | null => {
  const first = linkedEvidence(a, evidence)
    .map((e) => Date.parse(e.captured_at))
    .sort((x, y) => x - y)[0];
  return first ? first - Date.parse(a.created_at) : null;
};

/** The same measure over a set, ready for `median`. */
export const cycleTimesMs = (
  assignments: FieldAssignment[],
  evidence: Evidence[],
): number[] =>
  assignments
    .map((a) => timeToFirstEvidenceMs(a, evidence))
    .filter((n): n is number => n != null);
